/**
 * Lap Comparison Module
 *
 * Pre-computes expensive telemetry transformations for lap comparison views.
 * This processor caches coasting detection, time drift calculation, and
 * distance recalibration to avoid recomputing on every render.
 *
 * Used by both web and mobile frontends.
 */

// Types
export type {
  PreprocessedDataPoint,
  PreprocessedLapComparison,
  LapComparisonCache,
  LapComparisonStats,
  LapComparisonProcessorOptions,
  BaseLapDataPoint,
  RecalibrationRefData,
  PreprocessedGradientPoint,
  PreprocessedLapGradient,
  TimeDriftInterpolation,
} from './types'

// Main preprocessor
export {
  generateLapComparisonCacheKey,
  preprocessLapsForComparison,
} from './preprocessor'

// Statistics
export { calculateComparisonStats } from './statistics'

// Time drift interpolation
export {
  interpolateTimeDrift,
  interpolateTimeDriftWithRate,
} from './timeDriftInterpolation'

// Section time deltas (corner-complex net time gained/lost badges)
export {
  buildTimeSections,
  computeSectionDeltas,
  summarizeSectionDeltas,
  focusSectionBadges,
  createSectionCurveSampler,
  findRateHotspots,
  DEFAULT_SECTION_BADGE_OPTIONS,
  DEFAULT_SECTION_FOCUS_OPTIONS,
  DEFAULT_RATE_HOTSPOT_OPTIONS,
} from './sectionTimeDeltas'
export type {
  TimeSection,
  SectionLapDelta,
  SectionBadge,
  SectionBadgeOptions,
  SectionFocusOptions,
  FocusedSectionBadge,
  SectionCurveSampler,
  RateHotspot,
  RateHotspotOptions,
} from './sectionTimeDeltas'

// Badge classification (compensated / self-contained advice qualifiers)
export {
  classifySectionBadges,
  resolveLookaheadLength,
  DEFAULT_SECTION_CLASSIFICATION_OPTIONS,
} from './sectionBadgeClassification'
export type {
  SectionBadgeClassification,
  ClassifiedSectionBadge,
  SectionClassificationOptions,
} from './sectionBadgeClassification'

// Two-phase processing (for React hooks optimization)
export {
  computeBaseDataPoints,
  applyTimeDriftOverlay,
} from './twoPhaseProcessing'
